import { defineComponent, PropType, Slot, useSlots, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import * as _ from 'lodash';
import ModalView from './ModalView.vue';
import { CloseModalButtonVariants, isCloseModalButtonVariant } from './modalTypes';

interface Props {
  isOpen: boolean;
  isFullScreen?: boolean;
  showHeader?: boolean;
  modalId: string;
  closeModalButtonVariants?: CloseModalButtonVariants;
  closeOnOutsideClick?: boolean;
}

export interface ModalRef {
  closeModal: () => Promise<string>;
}

const Modal = defineComponent({
  props: {
    modalId: {
      type: String,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    isFullScreen: {
      type: Boolean,
      optional: true,
      default: false,
    },
    showHeader: {
      type: Boolean,
      optional: true,
      default: true,
    },
    closeModalButtonVariant: {
      type: String as PropType<CloseModalButtonVariants>,
      optional: true,
      validate: (value: string) => isCloseModalButtonVariant(value),
      default: CloseModalButtonVariants.dark,
    },
    closeOnOutsideClick: {
      type: Boolean,
      optional: true,
      default: true,
    },
  },
  emits: ['close', 'update:isOpen'],
  async setup(props: Props, { emit, expose }) {
    const slots = useSlots();
    if (!slots.default) {
      throw new Error('Modal must have a default slot');
    }

    // Get only the slots that are not undefined
    const modalSlots: Record<string, Slot> = _.reduce(
      slots,
      (result: Record<string, Slot>, value: Slot | undefined, slotName: string) => {
        if (value) {
          return {
            ...result,
            [slotName]: value,
          };
        }
        return result;
      },
      {},
    );

    const closeModal = () => {
      emit('update:isOpen', false);
    };

    const { open, close, options: modalState } = useModal({
      component: ModalView,
      attrs: {
        teleportTo: false,
        modalId: props.modalId,
        showHeader: props.showHeader,
        isFullScreen: props.isFullScreen,
        closeModalButtonVariant: props.closeModalButtonVariants,
        closeOnOutsideClick: props.closeOnOutsideClick,
        closeModal,
      },
      slots: modalSlots,
    });

    watch(() => props.isOpen, async newValue => {
      if (newValue) {
        await open();
      } else if (modalState.modelValue) {
        await close();
      }
    });

    expose({ closeModal });

    if (props.isOpen) {
      await open();
    }
  },
  render() { return null; },
});

export default Modal;
